// Frontend salary currency constants.
//
// Keep these in sync with the backend `config/salary.php` (currencies + default).
// The backend is the source of truth and validates `salary_currency` with
// `in:CHF,EUR,USD`; this mirror lets the UI offer the same options and guard
// against unsupported values before submitting.

export const SALARY_CURRENCIES = ['CHF', 'EUR', 'USD']

export const DEFAULT_SALARY_CURRENCY = 'CHF'

// Options shaped for the <Select> / dropdown components (label === value because
// currency codes are universal ISO 4217 codes and are not translated).
export const SALARY_CURRENCY_OPTIONS = SALARY_CURRENCIES.map(currency => ({
	label: currency,
	value: currency
}))

// Returns a supported currency code, falling back to the default for missing or
// unsupported values (e.g. legacy records saved before the currency feature).
export const normalizeSalaryCurrency = value =>
	SALARY_CURRENCIES.includes(value) ? value : DEFAULT_SALARY_CURRENCY

export const isSupportedSalaryCurrency = value =>
	SALARY_CURRENCIES.includes(value)

const SALARY_INPUT_GROUP_SEPARATORS = {
	CHF: "'",
	EUR: '.',
	USD: ','
}

const escapeRegExp = value =>
	String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const isSalaryInputKeyAllowed = event => {
	if (event?.ctrlKey || event?.metaKey || event?.altKey) {
		return true
	}

	const key = event?.key

	return !key || key.length > 1 || /^\d$/.test(key)
}

export const isSalaryInputPasteAllowed = event => {
	const pastedText = event?.clipboardData?.getData('text')

	return pastedText === undefined || /^\d+$/.test(pastedText)
}

export const parseSalaryInputValue = (
	value,
	currency = DEFAULT_SALARY_CURRENCY
) => {
	if (value === null || value === undefined || value === '') {
		return null
	}

	const normalizedCurrency = normalizeSalaryCurrency(currency)
	const separator = SALARY_INPUT_GROUP_SEPARATORS[normalizedCurrency]
	const raw = String(value).replace(/’/g, "'")
	const allowedCharacters = new RegExp(`^[\\d${escapeRegExp(separator)}]+$`)

	if (!allowedCharacters.test(raw)) {
		return null
	}

	const normalizedAmount = raw.split(separator).join('')

	if (!/^\d+$/.test(normalizedAmount)) {
		return null
	}

	const parsed = Number(normalizedAmount)

	return Number.isNaN(parsed) ? null : parsed
}

export const formatSalaryInputValue = (
	value,
	currency = DEFAULT_SALARY_CURRENCY
) => {
	const amount = parseSalaryInputValue(value, currency)

	if (amount === null || Number.isNaN(amount)) {
		return ''
	}

	const separator =
		SALARY_INPUT_GROUP_SEPARATORS[normalizeSalaryCurrency(currency)]

	return String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}
